/**
 * Changelog API Client
 *
 * HTTP client for edit sessions and undo/redo operations.
 * backend-contracts-changelog-undo-redo-api-R04; changelog-undo-redo-undo-redo-R31.
 */

import { httpClient } from "./client";
import {
  assertObject,
  assertString,
  assertNumber,
  assertBoolean,
  assertArrayOf,
} from "../types/validation";

// ============================================================================
// Types
// ============================================================================

export interface IEditSessionResponse {
  readonly id: number;
  readonly clientId: string;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
}

/**
 * Actual undo/redo availability of the current session, as recorded by the journal.
 *
 * "Can undo" means the session has at least one non-undone action; "can redo" —
 * at least one undone action that is still redoable. A change that produced no
 * undoable action does not move either flag.
 */
export interface IUndoRedoState {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoPrivilegeGranted: boolean;
  readonly redoPrivilegeGranted: boolean;
}

export interface IUndoRedoResult {
  readonly success: boolean;
  readonly actionGroupId: string;
  /**
   * Перечень затронутых сущностей — доменными видами, как их видит пользователь
   * (undo-redo-R18; реестр видов — spec/backend/invariants/audit.md, audit-R12).
   * Имён хранения продукт здесь не отдаёт; виды названы по-русски при любом
   * языке интерфейса (контракт changelog-undo-redo-api 2.4.0).
   */
  readonly affectedEntityKinds: readonly string[];
  readonly affectedRowCount: number;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
}

// Типа записи «истории сеанса» в контракте нет: отдельной поверхности продукт
// не предусматривает (spec/backend/changelog-undo-redo/edit-sessions.md,
// основание: вердикт 240).


export interface ICreateEditSessionRequest {
  readonly clientId: string;
  readonly projectId: number;
}

// ============================================================================
// Runtime Validators
// ============================================================================

function validateEditSessionResponse(data: unknown, path: string): IEditSessionResponse {
  const obj = assertObject(data, path);
  return {
    id: assertNumber(obj['id'], `${path}.id`),
    clientId: assertString(obj['clientId'], `${path}.clientId`),
    canUndo: assertBoolean(obj['canUndo'], `${path}.canUndo`),
    canRedo: assertBoolean(obj['canRedo'], `${path}.canRedo`),
  };
}

function validateUndoRedoState(data: unknown, path: string): IUndoRedoState {
  const obj = assertObject(data, path);
  return {
    canUndo: assertBoolean(obj['canUndo'], `${path}.canUndo`),
    canRedo: assertBoolean(obj['canRedo'], `${path}.canRedo`),
    undoPrivilegeGranted: assertBoolean(obj['undoPrivilegeGranted'], `${path}.undoPrivilegeGranted`),
    redoPrivilegeGranted: assertBoolean(obj['redoPrivilegeGranted'], `${path}.redoPrivilegeGranted`),
  };
}

function validateUndoRedoResult(data: unknown, path: string): IUndoRedoResult {
  const obj = assertObject(data, path);
  return {
    success: assertBoolean(obj['success'], `${path}.success`),
    actionGroupId: assertString(obj['actionGroupId'], `${path}.actionGroupId`),
    affectedEntityKinds: assertArrayOf(
      obj['affectedEntityKinds'],
      (item, p) => assertString(item, p),
      `${path}.affectedEntityKinds`,
    ),
    affectedRowCount: assertNumber(obj['affectedRowCount'], `${path}.affectedRowCount`),
    canUndo: assertBoolean(obj['canUndo'], `${path}.canUndo`),
    canRedo: assertBoolean(obj['canRedo'], `${path}.canRedo`),
  };
}


// ============================================================================
// API Client Interface
// ============================================================================

export interface IChangelogApiClient {
  /** Open a new edit session */
  createEditSession(request: ICreateEditSessionRequest): Promise<IEditSessionResponse>;

  /** Heartbeat — keep session alive */
  pingEditSession(): Promise<void>;

  /** Close edit session */
  closeEditSession(): Promise<void>;

  /** Read actual undo/redo availability from the session journal */
  getUndoRedoState(): Promise<IUndoRedoState>;

  /** Undo last action */
  undo(): Promise<IUndoRedoResult>;

  /** Redo last undone action */
  redo(): Promise<IUndoRedoResult>;

  /** Get action history for current session */
}

// ============================================================================
// API Client Implementation
// ============================================================================

export class ChangelogApiClient implements IChangelogApiClient {
  async createEditSession(request: ICreateEditSessionRequest): Promise<IEditSessionResponse> {
    const response = await httpClient.post<unknown>(
      "/edit-sessions",
      request,
    );
    return validateEditSessionResponse(response.data, 'createEditSession');
  }

  async pingEditSession(): Promise<void> {
    await httpClient.put("/edit-sessions/ping");
  }

  async closeEditSession(): Promise<void> {
    await httpClient.post("/edit-sessions/close");
  }

  async getUndoRedoState(): Promise<IUndoRedoState> {
    const response = await httpClient.get<unknown>("/edit-sessions/state");
    return validateUndoRedoState(response.data, 'getUndoRedoState');
  }

  async undo(): Promise<IUndoRedoResult> {
    const response = await httpClient.post<unknown>("/undo");
    return validateUndoRedoResult(response.data, 'undo');
  }

  async redo(): Promise<IUndoRedoResult> {
    const response = await httpClient.post<unknown>("/redo");
    return validateUndoRedoResult(response.data, 'redo');
  }

}

// ============================================================================
// Singleton Instance
// ============================================================================

let changelogApiClient: ChangelogApiClient | null = null;

export function getChangelogApiClient(): ChangelogApiClient {
  changelogApiClient ??= new ChangelogApiClient();
  return changelogApiClient;
}
